import { useEffect, useRef, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import ROUTES from '@/ROUTES'

import { IRoadmapStep } from '@/MODELS/roadmapStep.model'

import analytics from '@/services/analytics.service'
import { useAuth } from '@/services/authentication.service'
import { useChat } from '@/services/chat.service'
import { constructChatContext } from '@/services/prompts'
import { useResource } from '@/services/resource.service'

import ChatSidebar from '@/components/chat/ChatSidebar'
import ComposeMessage from '@/components/chat/ComposeMessage'
import { Conversation } from '@/components/chat/Conversation'
import { SectionHeader } from '@/components/SectionHeader'

export default function Chat() {
    const { user } = useAuth()
    const [searchParams] = useSearchParams()
    const location = useLocation()
    const roadMapId = searchParams.get('roadmap')
    const prefillMessage = searchParams.get('prefill')

    const { chat, streamingMessage, setActiveChat, startPresetChat, userChatHistory, deleteChat, saveChat } = useChat()
    const conversationEndRef = useRef(null)
    const initializationRef = useRef<string | null>(null)
    const autoSaveTimeoutRef = useRef<NodeJS.Timeout>()
    const previousMessageCountRef = useRef(0)

    const [isTyping, setIsTyping] = useState(false)
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [isMobile, setIsMobile] = useState(false)

    const [lastSavedMessageCount, setLastSavedMessageCount] = useState(0)
    // Fetch the specific roadmap step when navigating from a roadmap card (e.g., /chat?roadmap=abc123).
    // When roadMapId is absent, the endpoint still resolves but returns 400 (invalid ID) — harmless,
    // and the result is guarded with `if (!roadmap)` at the usage site.
    const [roadmap, _, RoadmapResource] = useResource<IRoadmapStep>(`${ROUTES.app.roadmap}/${roadMapId || 'none'}`)

    const showPlaceholder = chat?.messages?.length <= 0 && !streamingMessage && !isTyping && !roadMapId

    const shouldAutoSave = (chat: any) => {
        if (!chat?.messages) return false
        const userMessages = chat.messages.filter((msg) => msg.role === 'user')
        const assistantMessages = chat.messages.filter((msg) => msg.role === 'assistant')
        return userMessages.length > 0 && assistantMessages.length > 0
    }

    async function handleAutoSave(currentChat) {
        if (!currentChat || !shouldAutoSave(currentChat)) return

        const currentMessageCount = currentChat.messages?.length || 0

        if (currentMessageCount === lastSavedMessageCount) return

        if (streamingMessage !== null) return

        try {
            const title = formatChatTitle(currentChat)
            await saveChat(title)
            setLastSavedMessageCount(currentMessageCount)
        } catch (error) {
            console.error('Auto-save failed:', error)
        }
    }

    useEffect(() => {
        if (!chat || !shouldAutoSave(chat)) return

        if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current)
        }

        autoSaveTimeoutRef.current = setTimeout(() => {
            handleAutoSave(chat)
        }, 3000)

        return () => {
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current)
            }
        }
    }, [chat?.messages?.length, streamingMessage])

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (chat && shouldAutoSave(chat)) {
                handleAutoSave(chat)
            }
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden' && chat && shouldAutoSave(chat)) {
                handleAutoSave(chat)
            }
        }

        window.addEventListener('beforeunload', handleBeforeUnload)
        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload)
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [chat])

    function handleChatSelect(chatId) {
        if (chat && shouldAutoSave(chat)) {
            handleAutoSave(chat)
        }

        setActiveChat(chatId)
        setSidebarOpen(false)
        // Seed lastSavedMessageCount with the loaded chat's count so the auto-save
        // effect doesn't fire 3s later just because we opened the chat. Otherwise
        // a no-edit view re-saves the chat and bumps its updatedAt, sending it to
        // the top of the sidebar.
        setLastSavedMessageCount(userChatHistory[chatId]?.messages?.length || 0)
    }

    function handleNewChat() {
        if (chat && shouldAutoSave(chat)) {
            handleAutoSave(chat)
        }

        setActiveChat(null)
        setLastSavedMessageCount(0)
    }

    useEffect(() => {
        return () => {
            if (chat && shouldAutoSave(chat)) {
                handleAutoSave(chat)
            }
        }
    }, [location.pathname])

    useEffect(() => {
        if (roadMapId && initializationRef.current !== roadMapId) {
            setActiveChat(null)
            initializationRef.current = null
        }

        return () => {
            if (roadMapId) {
                setActiveChat(null)
                initializationRef.current = null
            }
        }
    }, [roadMapId])

    useEffect(() => {
        if (!roadMapId || initializationRef.current === roadMapId) return
        if (!roadmap || !user?.userInfo) return
        if (chat?.messages?.length > 0) return
        if (streamingMessage !== null) return

        initializationRef.current = roadMapId
        analytics.trackChatSessionStart(true, roadMapId)
        startPresetChat(constructChatContext(roadmap, user.userInfo))
    }, [roadMapId, initializationRef, roadmap])

    useEffect(() => {
        if (!roadMapId && chat?.messages?.length === 1 && !streamingMessage) {
            analytics.trackChatSessionStart(false)
        }
    }, [chat?.messages?.length, roadMapId, streamingMessage])

    useEffect(() => {
        return () => {
            if (chat?.messages?.length > 0) {
                analytics.trackChatSessionEnd()
            }
        }
    }, [])

    useEffect(() => {
        if (showPlaceholder) return

        const currentMessageCount = chat?.messages?.length || 0

        if (currentMessageCount > previousMessageCountRef.current) {
            const lastMessage = chat?.messages?.[currentMessageCount - 1]

            if (lastMessage?.role === 'user' && conversationEndRef.current) {
                conversationEndRef.current.scrollIntoView({ behavior: 'smooth' })
            }
        }

        previousMessageCountRef.current = currentMessageCount
    }, [chat?.messages])

    const handleDeleteChat = (e, chatId) => {
        e.stopPropagation()
        deleteChat(chatId)
    }

    useEffect(() => {
        function checkMobile() {
            setIsMobile(window.innerWidth < 768)
        }

        checkMobile()
        window.addEventListener('resize', checkMobile)
        return () => window.removeEventListener('resize', checkMobile)
    }, [])

    function formatChatTitle(messages) {
        if (messages && messages.length > 0) {
            const firstMessage = messages.find((msg) => msg.role === 'user')
            if (firstMessage) {
                return firstMessage.content.slice(0, 28) + (firstMessage.content.length > 28 ? '...' : '')
            }
        }
        return 'New Chat'
    }

    const filteredHistory =
        Object.values(userChatHistory)
            .filter((chatItem) => formatChatTitle(chatItem.messages).toLowerCase().includes(searchQuery.toLowerCase()))
            .sort((a, b) => {
                const dateA = new Date(a.updatedAt || a.createdAt || 0)
                const dateB = new Date(b.updatedAt || b.createdAt || 0)
                return dateB.getTime() - dateA.getTime()
            }) || []

    return (
        <div className={`flex h-[calc(100vh-4rem)] md:h-screen relative ${isMobile ? 'chat-container' : ''}`}>
            {sidebarOpen && (
                <div
                    className='fixed inset-0 z-20 transition-opacity duration-300 bg-black/20 backdrop-blur-sm lg:hidden'
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <ChatSidebar
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
                searchQuery={searchQuery}
                handleChatSelect={handleChatSelect}
                handleDeleteChat={handleDeleteChat}
                activeChat={chat}
                filteredHistory={filteredHistory}
                setActiveChat={setActiveChat}
                setSearchQuery={setSearchQuery}
            />

            <div className='flex flex-col flex-1 min-w-0'>
                <SectionHeader
                    variant='chat'
                    title={chat ? formatChatTitle(chat.messages) : 'New Chat'}
                    isMobile={isMobile}
                    onSidebarToggle={() => setSidebarOpen(true)}
                    onNewChat={handleNewChat}
                />

                <div className='flex-1 pb-5 overflow-x-hidden overflow-y-auto'>
                    <div className='max-w-4xl mx-auto'>
                        <Conversation showQuickQuestions={showPlaceholder} conversationWrapperClass='px-4 py-6' />
                        <div ref={conversationEndRef} className='h-4' />
                    </div>
                </div>

                <div className='flex-shrink-0 px-2 border-t bg-white/80 backdrop-blur-xl border-gray-200/50 compose-wrapper'>
                    <div className='px-4 pt-2 pb-20'>
                        <ComposeMessage onChange={(typing) => setIsTyping(typing)} prefillMessage={prefillMessage} />
                    </div>
                </div>
            </div>
        </div>
    )
}
